import { createContext } from "react";
import { UserRole } from "@/types/user/user-role";
import EphemeralKey from "@/utilities/crypto/EphemeralKey";
import KeyPair from "@/utilities/crypto/KeyPair";
import StudentKeyPair from "@/utilities/crypto/StudentKeyPair";
import TeacherLongTermKeyPair from "@/utilities/crypto/TeacherLongTermKeyPair";

type SerializedTeacherKeyPair<
  T extends {
    keyPair: KeyPair;
  },
> = Omit<T, "keyPair"> & {
  keyPair: {
    privateKey: JsonWebKey;
    publicKey: JsonWebKey;
  };
  saltPublicKey: JsonWebKey;
};

type SerializedStudentKeyPair<
  T extends {
    keyPair: KeyPair;
  },
> = Omit<T, "keyPair" | "teacherPublicKey" | "ephemeralKey"> & {
  keyPair: {
    privateKey: JsonWebKey;
    publicKey: JsonWebKey;
  };
  saltPublicKey: JsonWebKey;
  // the ephemeral key is not serialized
  ephemeralKey: undefined;
  // but the teacher public key is which allows deriving it again
  teacherPublicKey: JsonWebKey;
};

type AuthenticationVersion2 = {
  version: "2";
};

type Unauthenticated = AuthenticationVersion2 & {
  idToken: undefined;
  authenticationToken: undefined;
  role: undefined;
};

export type AdminOrTeacherAuthenticated = AuthenticationVersion2 & {
  idToken: string;
  authenticationToken: string;
  userId: number;
  name: string;
  email: string;
  role: UserRole.teacher | UserRole.admin;
  keyPair: TeacherLongTermKeyPair;
  keyPairId: number;
};

export type StudentLocallyAuthenticated = AuthenticationVersion2 & {
  isAnonymous: false;
  idToken: string;
  studentId: number | undefined;
  authenticationToken: undefined;
  name: string;
  role: UserRole.student;
};

export type StudentAuthenticated = Omit<
  StudentLocallyAuthenticated,
  "authenticationToken"
> & {
  // a student is always authenticated with respect to a session
  sessionId: number;
  studentId: number;
  teacherPublicKey: JsonWebKey;
  authenticationToken: string;
  keyPair: StudentKeyPair;
  ephemeralKey: EphemeralKey;
  // the encrypted pseudonym (base64), used to generate the student's nickname
  pseudonym: string;
};

export type StudentAuthenticatedAnonymous = Omit<
  StudentAuthenticated,
  "idToken" | "ephemeralKey" | "isAnonymous" | "pseudonym"
> & {
  isAnonymous: true;
  idToken: undefined;
  ephemeralKey: undefined;
  pseudonym: undefined;
};

export type AuthenticationContextType =
  | Unauthenticated
  | AdminOrTeacherAuthenticated
  | StudentLocallyAuthenticated
  | StudentAuthenticated
  | StudentAuthenticatedAnonymous;

type SerializedAuthenticationContextTypeV1 =
  | Unauthenticated
  | SerializedTeacherKeyPair<AdminOrTeacherAuthenticated>
  | StudentLocallyAuthenticated
  | SerializedStudentKeyPair<StudentAuthenticated>
  | SerializedStudentKeyPair<StudentAuthenticatedAnonymous>;

export type SerializedAuthenticationContextType =
  SerializedAuthenticationContextTypeV1;

export const latestAuthenticationContextVersion = "2" as const;

export const isStudentLocallyAuthenticated = (
  authContext: AuthenticationContextType,
): authContext is StudentLocallyAuthenticated =>
  authContext.version === latestAuthenticationContextVersion &&
  authContext.role === UserRole.student &&
  authContext.idToken !== undefined &&
  authContext.authenticationToken === undefined;

export const isStudentFullyAuthenticated = (
  authContext: AuthenticationContextType,
  sessionId: number,
): authContext is StudentAuthenticated | StudentAuthenticatedAnonymous =>
  authContext.version === latestAuthenticationContextVersion &&
  authContext.role === UserRole.student &&
  authContext.authenticationToken !== undefined &&
  authContext.sessionId === sessionId;

export const isStudentAuthenticated = (
  authContext: AuthenticationContextType,
): authContext is
  | StudentLocallyAuthenticated
  | StudentAuthenticated
  | StudentAuthenticatedAnonymous =>
  authContext.version === latestAuthenticationContextVersion &&
  authContext.role === UserRole.student &&
  // if the authentication token is set, the student is fully authenticated
  // if not, but the id token is set, the student is locally authenticated
  (authContext.authenticationToken !== undefined ||
    authContext.idToken !== undefined);

export const isFullyAuthenticated = (
  authContext: AuthenticationContextType,
): authContext is
  | AdminOrTeacherAuthenticated
  | StudentAuthenticated
  | StudentAuthenticatedAnonymous =>
  authContext.version === latestAuthenticationContextVersion &&
  authContext.authenticationToken !== undefined;

export const isAdminOrTeacher = (
  authContext: AuthenticationContextType,
): authContext is AdminOrTeacherAuthenticated =>
  isFullyAuthenticated(authContext) &&
  (authContext.role === UserRole.teacher ||
    authContext.role === UserRole.admin);

/**
 * Whether the authenticated user may share/copy the join link of a session
 * belonging to the given class: admins may share any class's sessions, teachers
 * only their own.
 */
export const canShareSession = (
  authContext: AuthenticationContextType,
  klass: { teacher: { id: number } },
): authContext is AdminOrTeacherAuthenticated =>
  isAdminOrTeacher(authContext) &&
  (authContext.role === UserRole.admin ||
    klass.teacher.id === authContext.userId);

export const authenticationContextDefaultValue: AuthenticationContextType = {
  version: latestAuthenticationContextVersion,
  idToken: undefined,
  authenticationToken: undefined,
  role: undefined,
};

export const AuthenticationContext = createContext<AuthenticationContextType>(
  authenticationContextDefaultValue,
);

export const serializeAuthenticationContext = async (
  authContext: AuthenticationContextType,
): Promise<SerializedAuthenticationContextType> => {
  if (!("keyPair" in authContext)) {
    return authContext;
  }

  const { keyPair, ...rest } = authContext;

  if (rest.role === UserRole.student) {
    return {
      ...rest,
      ephemeralKey: undefined,
      keyPair: await keyPair.exportUnprotected(),
      saltPublicKey: await keyPair.exportSaltPublicKey(),
      teacherPublicKey: rest.teacherPublicKey,
    };
  } else {
    return {
      ...rest,
      keyPair: await keyPair.exportUnprotected(),
      saltPublicKey: await keyPair.exportSaltPublicKey(),
    };
  }
};

export const deserializeAuthenticationContext = async (
  crypto: SubtleCrypto,
  serializedContext: SerializedAuthenticationContextType,
): Promise<AuthenticationContextType> => {
  if (!("keyPair" in serializedContext)) {
    return serializedContext;
  }

  const { keyPair, saltPublicKey, ...rest } = serializedContext;

  if (rest.version !== latestAuthenticationContextVersion) {
    // if the version does not match, we cannot be sure that the deserialization is correct
    // we return the default unauthenticated context, which will trigger a new authentication flow
    return authenticationContextDefaultValue;
  }

  const importedCryptoKeyPair = await KeyPair.importUnprotected(
    crypto,
    keyPair,
  );

  const importedSaltPublicKey = await crypto.importKey(
    "jwk",
    saltPublicKey,
    KeyPair.ImportAlgorithm,
    true,
    [],
  );

  const importedKeyPair = await StudentKeyPair.create(
    crypto,
    importedCryptoKeyPair,
    importedSaltPublicKey,
  );

  if (rest.role === UserRole.student) {
    const { studentId } = rest;

    if (rest.isAnonymous) {
      return {
        ...rest,
        studentId,
        teacherPublicKey: rest.teacherPublicKey,
        keyPair: importedKeyPair,
      } satisfies StudentAuthenticatedAnonymous;
    }

    return {
      ...rest,
      studentId,
      ephemeralKey: await importedKeyPair.deriveSharedEphemeralKey(
        rest.teacherPublicKey,
        // we already authenticated the teacher's public key
        await KeyPair.getJsonWebKeyKeyFingerprint(
          crypto,
          rest.teacherPublicKey,
        ),
      ),
      teacherPublicKey: rest.teacherPublicKey,
      keyPair: importedKeyPair,
    } satisfies StudentAuthenticated;
  }

  return {
    ...rest,
    keyPair: await TeacherLongTermKeyPair.create(
      crypto,
      importedCryptoKeyPair,
      importedSaltPublicKey,
    ),
  };
};
